import {InteractionManager, XR} from "@react-three/xr";
import {Canvas} from "@react-three/fiber";
import React, {FunctionComponent, useEffect, useState} from "react";
import {RootState} from "@react-three/fiber/dist/declarations/src/core/store";
import {XRSession, XRSessionInit} from "three";
import {initAR, isARSupported} from "../../utils/ARSupportCheck";
import {ErrorImpact, WithErrorHandling} from "../../interfaces/ErrorHandling";

export interface XRSessionInitWithDomOverlay extends XRSessionInit {
    domOverlay?: {
        root: Element;
    };
}

export type WithInitialize = {
    initialize: boolean;
};

export type MyARCanvasProps = {
    sessionInit?: XRSessionInitWithDomOverlay;
    onStarted?: (rootState: RootState, session: XRSession) => void;
    onEnded?: () => void;
    domOverlay?: Element | null;
    initialize: boolean;
}

export const CustomARCanvas: FunctionComponent<MyARCanvasProps & WithErrorHandling & WithInitialize> = (props) => {
    const [isSupported, setIsSupported] = useState<boolean | null>(null);
    const [rs, setRs] = useState<RootState | null>(null);
    const [xrSession, setXrSession] = useState<XRSession | null>(null);

    const { onEnded } = props;

    useEffect(() => {
        isARSupported()
            .then((result) => setIsSupported(result))
            .catch((error) => {
                props.onError({
                    error,
                    impact: ErrorImpact.CRITICAL,
                });
                setIsSupported(false)
            });
    });

    useEffect(() => {
        if (xrSession) {
            const onEndedWrapper = () => {
                xrSession.removeEventListener('end', onEndedWrapper);
                onEnded?.();
                setXrSession(null);
            };

            xrSession.addEventListener('end', onEndedWrapper);

            return () => {
                xrSession.removeEventListener('end', onEndedWrapper);
            };
        }
    }, [onEnded, xrSession]);

    const onClickStart = async (rootState: RootState) => {

        const {gl} = rootState;
        const sessionInit = {
            ...props.sessionInit
        };

        if (!sessionInit.domOverlay && props.domOverlay) {

            if (!sessionInit.requiredFeatures) {
                sessionInit.requiredFeatures = [];
            }

            if (!sessionInit.requiredFeatures.includes("dom-overlay")) {
                sessionInit.requiredFeatures.push("dom-overlay");
            }

            sessionInit.domOverlay = {
                root: props.domOverlay,
            };
        }

        try {
            const session = await initAR(gl, sessionInit);
            setXrSession(session);
            props.onStarted?.(rootState, session);
        } catch (e) {
            alert(`Could not start AR session: ${e.message}`);
        }
    };

    useEffect(() => {

        if (props.initialize && rs) {
            onClickStart(rs).catch(e => console.error(e));
        }

    }, [props.initialize, rs]);

    if (isSupported === null) {

        return (
            <div style={{color: "white"}}>
                Hold tight! Checking AR compatibility...
            </div>
        );
    }

    if (!isSupported) {

        props.onError({
            error: new Error("Device not supported"),
            impact: ErrorImpact.CRITICAL,
        });

        return null;
    }

    return (
        <Canvas vr onCreated={(rootState: RootState) => setRs(rootState)} style={{visibility: 'hidden', position: 'fixed'}}>
            <XR>
                <InteractionManager>
                    {props.children}
                </InteractionManager>
            </XR>
        </Canvas>
    )
};
